import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod/v4";
import { api } from "@/lib/http";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/loading";
import { generateDefaultValue } from "@/lib/utils/zod";

const contactUsSearchSchema = z.object({
  source: z.string().optional(),
});
export const Route = createFileRoute("/_app/contact-us")({
  component: RouteComponent,
  validateSearch: contactUsSearchSchema,
  staticData: {
    title: "Contact Us",
  },
});

const ContactUsFormSchema = z.object({
  full_name: z.string(),
  email: z.email(),
  company: z.string(),
  message: z.string(),
  phone_number: z
    .string()
    .max(15, "Phone number cannot be longer than 15 characters")
    .optional(),
  appeal_type: z.literal("I would like to Schedule a Demo"),
  source: z.string().optional(),
});
type ContactUsFormSchema = z.infer<typeof ContactUsFormSchema>;

function RouteComponent() {
  const { source } = Route.useSearch();

  const form = useForm({
    resolver: standardSchemaResolver(ContactUsFormSchema),
    defaultValues: {
      ...generateDefaultValue(ContactUsFormSchema),
      appeal_type: "I would like to Schedule a Demo",
      source,
    },
  });

  useEffect(() => {
    form.setValue("source", source);
  }, [source]);

  const submitContact = useMutation({
    async mutationFn(json: ContactUsFormSchema) {
      await api.auth.post("contact/", {
        json,
      });
    },
    onSuccess: () => toast.success("Request submitted successfully!"),
  });

  return (
    <div className="mx-auto my-auto max-h-100 max-w-200 justify-self-center-safe md:mt-10">
      <h1 className="mb-5 scroll-m-20 text-2xl font-bold tracking-tight lg:text-5xl">
        The page you tried to access is not available yet, but you can request a
        demo here:
      </h1>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((formData) =>
            submitContact.mutate(formData),
          )}
          className="*:my-2"
        >
          <FormField
            control={form.control}
            name="full_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>* Full Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>* Email</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone number</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your Message</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button disabled={submitContact.isPending} type="submit">
            {submitContact.isPending ? (
              <>
                <LoadingSpinner /> Submitting...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
